import { Typography, Divider, Stack, Link } from '@mui/material';

export default function CookiePolicyEn() {
  return (
    <Stack spacing={3}>
      <Typography variant="h4" fontWeight={700}>
        BRITESTAGE – Cookie Policy
      </Typography>
      <Typography variant="body2" color="text.secondary">
        <strong>Version 1.0</strong>
        <br />
        Last updated: 30th April 2026
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        1. Who is responsible?
      </Typography>
      <Typography variant="body1">
        The controller within the meaning of the EU General Data Protection Regulation (GDPR) and applicable national
        laws implementing the EU rules on cookies and similar technologies is:
      </Typography>
      <Typography variant="body1">
        <strong>21Minds GmbH</strong>
        <br />
        Botnanger Str. 6
        <br />
        70193 Stuttgart
        <br />
        Germany
        <br />
        (&quot;BRITESTAGE&quot;, &quot;we&quot;, &quot;us&quot;)
      </Typography>
      <Typography variant="body1">
        Contact:{' '}
        <Link href="mailto:info@britestage.com" color="primary">
          info@britestage.com
        </Link>
      </Typography>
      <Typography variant="body1">
        This Cookie Policy should be read together with our{' '}
        <Link href="/legal/privacy?lang=en" color="primary">
          Privacy Policy
        </Link>
        , which contains further information on personal data processing, your rights, and international transfers where
        relevant.
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        2. What are cookies and similar technologies?
      </Typography>
      <Typography variant="body1">
        Cookies are small text files stored on your device when you visit a website. Similar technologies include, for
        example, pixels, scripts, or browser storage mechanisms (such as local storage or session storage) that can read
        or write information on your device for similar purposes.
      </Typography>
      <Typography variant="body1">
        We use both cookies and limited browser storage where this is technically appropriate to operate the service.
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        3. What we use today (simple technical setup)
      </Typography>
      <Typography variant="body1">
        BRITESTAGE is deliberately kept technically lean. At present we do not operate proprietary advertising or
        cross-site marketing trackers on the platform, and we do not deploy third-party analytics cookies (such as
        audience measurement tools) in the product.
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        3.1 Strictly necessary cookies and authentication (Clerk)
      </Typography>
      <Typography variant="body1">
        We use Clerk (&quot;Clerk, Inc.&quot;) as our authentication and session provider. When you sign in or create an
        account, Clerk sets cookies that are required to keep you logged in securely, protect accounts, and prevent
        abuse. Without these technologies, the platform cannot provide password-protected access.
      </Typography>
      <Typography variant="body1">
        Clerk processes data on our behalf as a service provider (processor) under the GDPR. Information about
        Clerk&apos;s use of cookies and similar technologies is available in Clerk&apos;s own documentation and privacy
        materials at{' '}
        <Link href="https://clerk.com/legal/privacy" target="_blank" rel="noopener noreferrer" color="primary">
          clerk.com/legal/privacy
        </Link>
        .
      </Typography>
      <Typography variant="body1">
        <strong>Legal basis:</strong> Art. 6(1)(b) GDPR (performance of the contract / steps prior to entering a
        contract) and, where applicable, Art. 6(1)(f) GDPR (legitimate interests in secure operation and fraud
        prevention), in line with EU and national rules that allow strictly necessary cookies without consent.
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        3.2 Browser storage for basic functionality (e.g. language)
      </Typography>
      <Typography variant="body1">
        We store certain non-sensitive preferences (for example your selected interface language) in the browser, using
        mechanisms such as local storage, so the application can load correctly on repeat visits. This is not used to
        track you across unrelated websites.
      </Typography>
      <Typography variant="body1">
        <strong>Legal basis:</strong> Art. 6(1)(b) GDPR and, where applicable, Art. 6(1)(f) GDPR (legitimate interest in
        a usable service), consistent with applicable exemptions for technologies that are strictly necessary for the
        requested functionality.
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        3.3 Optional categories and your saved preferences
      </Typography>
      <Typography variant="body1">
        During onboarding we may ask you to express preferences regarding broader categories (for example functional,
        analytical, or marketing cookies). Your choices can be stored in your user profile so we can respect them if we
        introduce optional technologies in the future.
      </Typography>
      <Typography variant="body1">
        <strong>Current product state:</strong> we do not use third-party advertising cookies or cross-site tracking for
        marketing on the platform. We do not use third-party analytics cookies in the current build. If this changes, we
        will update this Cookie Policy and, where the law requires it, obtain your consent before activating
        non-essential cookies.
      </Typography>
      <Typography variant="body1">
        <strong>Legal basis for storing your choices:</strong> Art. 6(1)(a) GDPR (consent), where consent is required
        for the relevant processing, and otherwise Art. 6(1)(b) or (f) GDPR as applicable.
      </Typography>

      <Typography variant="h6" fontWeight={600}>
        3.4 Payments and other third-party content
      </Typography>
      <Typography variant="body1">
        If you interact with payment or checkout experiences, the relevant payment service provider may set or read
        cookies or use similar technologies on its own domains. Such processing is governed by that provider&apos;s
        policies. We only integrate payment flows where needed to complete your transaction.
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        4. Retention
      </Typography>
      <Typography variant="body1">
        Session-related authentication cookies typically expire when you log out or after a limited security-related
        lifetime defined by our authentication provider. Persistent cookies or stored preferences may remain until they
        expire, you delete them, or you withdraw consent where consent applies. Exact lifetimes can vary by browser and
        provider configuration.
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        5. How you can control cookies
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>
            Use your browser settings to block or delete cookies. Note that blocking strictly necessary cookies may
            prevent sign-in or core features from working.
          </li>
          <li>
            Where we offer in-product controls for optional categories, you can adjust your choices there; any future
            consent banner will provide a clear way to accept or reject non-essential cookies in line with EU
            requirements.
          </li>
          <li>
            You may withdraw consent at any time where processing is based on consent, without affecting the lawfulness
            of processing based on consent before its withdrawal.
          </li>
        </ul>
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        6. Your rights and supervisory authority
      </Typography>
      <Typography variant="body1">
        You have the rights described in the GDPR (including access, rectification, erasure, restriction, objection
        where applicable, and data portability in certain cases). You also have the right to lodge a complaint with a
        supervisory authority, in particular in the Member State of your habitual residence, place of work, or place of
        the alleged infringement.
      </Typography>
      <Typography variant="body1">
        Details and contact information are set out in section 14 of our{' '}
        <Link href="/legal/privacy?lang=en" color="primary">
          Privacy Policy
        </Link>
        .
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        7. Changes
      </Typography>
      <Typography variant="body1">
        We may update this Cookie Policy when our use of cookies or similar technologies changes, or when laws or
        regulatory guidance evolve. The &quot;Last updated&quot; date at the top will be revised accordingly. Material
        changes will also be reflected in our Privacy Policy or in-product notices where appropriate.
      </Typography>
    </Stack>
  );
}
